import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { getUserType } from '../utils/userPermissions'

interface ProtectedRouteProps {
  children: ReactNode
  requireAuth?: boolean
  requireAdmin?: boolean
  redirectTo?: string
}

export default function ProtectedRoute({ 
  children, 
  requireAuth = true, 
  requireAdmin = false,
  redirectTo = '/login' 
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuthStore()
  const location = useLocation()

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  // If requireAuth is false (for login/register pages) and user is authenticated, redirect appropriately
  if (!requireAuth && isAuthenticated) {
    if (user?.role === 'ADMIN') {
      return <Navigate to="/admin" replace />
    }
    // Redirect government agencies to their dashboard
    if (getUserType(user) === 'AGENCY') {
      return <Navigate to="/gov/dashboard" replace />
    }
    // Redirect enterprise users to their appropriate dashboard
    const userType = getUserType(user)
    if (userType === 'ENTERPRISE_BUYER' || userType === 'ENTERPRISE_VENDOR') {
      return <Navigate to="/enterprise/dashboard" replace />
    }
    return <Navigate to="/" replace />
  }

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    // Save the attempted location so we can redirect back after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // If admin access is required but user is not admin
  if (requireAdmin && (!user || user.role !== 'ADMIN')) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
} 